import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueries } from '@tanstack/react-query';
import { useForm, useFormState } from 'react-hook-form';
import get from 'lodash/get';

import { Loader, Message } from '@components';
import { useRelatedMinMaxValidation } from '@hooks';
import {
  DatamodelFormField,
  FormError,
  FormFieldset,
  SelectField,
  TextField,
} from '@Forms/fields.jsx';
import { slugifyDash, slugifyOnBlur } from '@Forms/formatters.js';
import { errorsFromMutate } from '@Forms/utils.js';
import { datamodelQuery } from '@queries';
import { ButtonSpinnerIcon } from '@svgs';

const ContentDatabaseItemFormDetail = ({
  datamodelData,
  datamodelItemData,
  disabled,
  mutation,
  relatedDatamodels,
}) => {
  const useFormReturn = useForm({
    defaultValues: datamodelItemData,
    mode: 'onBlur',
  });
  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    setError,
    clearErrors,
    setFocus,
    setValue,
    watch,
  } = useFormReturn;
  const { isDirty, isValid } = useFormState({ control });
  useEffect(() => {
    // set focus to the first field if its new
    if (!datamodelItemData.id) {
      setFocus('name');
    }
  }, [datamodelItemData.id, setFocus]);

  const relatedModelListFields = useMemo(() => {
    return (datamodelData?.fields || [])
      .filter((f) => f.field_type === 'related_model_list')
      .map((f) => ({
        slug: f.slug,
        required: !!f.required,
        min_items: f.min_items,
        max_items: f.max_items,
      }));
  }, [datamodelData?.fields]);

  useRelatedMinMaxValidation({
    fields: relatedModelListFields,
    namePrefix: 'data',
    watch,
    errors,
    setError,
    clearErrors,
  });

  const onMutationError = (error) => {
    // shared mutation callback
    //
    // handle server-side validations and errors
    const errorsList = errorsFromMutate(error);
    errorsList.forEach(({ field, error }) => setError(field, error));
  };

  const onSubmit = async (data, e) => {
    mutation.mutate(data, {
      onError: (error) => onMutationError(error),
    });
  };

  // list of dynamic fields
  // prefix added to target custom_fields.

  const fieldsList = datamodelData.fields.map((field) => {
    const fieldName = `data.${field.slug}`;
    const fieldError = get(errors, fieldName);
    return (
      <div key={field.id} style={{ marginTop: 10 }}>
        <DatamodelFormField
          datamodelField={field}
          fieldPrefix={`data.`}
          relatedDatamodels={relatedDatamodels}
          useFormReturn={useFormReturn}
        />
        {field.field_type === 'related_model_list' && fieldError?.message && (
          <FormError message={fieldError.message} />
        )}
      </div>
    );
  });
  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className={
        `sb-manage-content-form ` + (disabled ? 'sb-form-disabled' : '')
      }
    >
      <FormFieldset>
        <TextField
          register={register}
          error={errors.name}
          options={{ required: 'This field is required' }}
          label="Item name"
          name="name"
          helpText="Name of the item"
          autoComplete="off"
        />
        {fieldsList}
      </FormFieldset>
      {datamodelItemData.id && (
        <FormFieldset legend="Advanced">
          <TextField
            register={register}
            error={errors.slug}
            options={{
              required: 'This field is required',
              onChange: slugifyDash,
              onBlur: (e) => {
                slugifyOnBlur(e);
                setValue('slug', e.target.value, {
                  shouldDirty: true,
                  shouldValidate: true,
                });
              },
            }}
            name="slug"
            label="Slug"
          />
        </FormFieldset>
      )}
      {true && (
        <div className="sb-manage-content-button-bar">
          <div className="sb-flex">
            <button
              type="submit"
              className="sb-btn sb-btn-save"
              disabled={disabled}
            >
              {!datamodelItemData.id && (
                <>{mutation.isPending ? 'Creating...' : 'Create new'}</>
              )}
              {datamodelItemData.id && (
                <>{mutation.isPending ? 'Publishing...' : 'Publish changes'}</>
              )}
              {mutation.isPending && <ButtonSpinnerIcon />}
            </button>
          </div>
          {!isValid && isDirty && (
            <Message messageStyle="error" title="Please review your form" />
          )}
          {mutation.isError && (
            <Message
              messageStyle="error"
              title="Error saving changes"
              message="Please review your form"
            />
          )}
          {mutation.isSuccess && (
            <Message messageStyle="success" title="Successfully saved" />
          )}
        </div>
      )}
    </form>
  );
};

const AddContentDatabaseItemForm = (props) => {
  // view that fetches data as needed then loads the detail form

  // get datamodel detail for the type
  const { data: datamodelData, isPending: datamodelIsPending } = useQuery(
    datamodelQuery(props.datamodelItemData.datamodel_id),
  );

  // get all the related datamodels used on fields
  const relatedDatamodels = useQueries({
    enabled: !datamodelIsPending,
    queries: datamodelData
      ? datamodelData.fields
          .filter((field) => Number.isInteger(field.related_datamodel_id))
          .map((field) => datamodelQuery(field.related_datamodel_id))
      : [],
  });
  // are all datamodel queries finished
  const allFinished = relatedDatamodels.every((query) => query.isSuccess);

  return (
    <>
      {(!allFinished || datamodelIsPending) && (
        <Loader className="sb-loader sb-loader-large" />
      )}
      {allFinished && datamodelData && (
        <ContentDatabaseItemFormDetail
          {...props}
          datamodelData={datamodelData}
          relatedDatamodels={relatedDatamodels}
        />
      )}
    </>
  );
};

const SelectContentTypeForm = (props) => {
  // select the content type before seeing all the fields

  const useFormReturn = useForm({
    defaultValues: {},
    mode: 'onBlur',
  });
  const {
    register,
    formState: { errors },
    setFocus,
  } = useFormReturn;

  useEffect(() => {
    // set focus to the first field
    setFocus('type');
  }, [setFocus]);

  // change event on page type toggle
  const typeToAddChange = (e) => {
    props.setDatamodelID(e.target.value);
  };

  // related datamodel choices for dropdown
  const typeChoices = props.datamodelsData.objects.map((datamodel) => {
    return (
      <option key={datamodel.id} value={datamodel.id}>
        {datamodel.name}
      </option>
    );
  });

  return (
    <form
      className={
        `sb-manage-content-form ` + (props.disabled ? 'sb-form-disabled' : '')
      }
    >
      <FormFieldset>
        <SelectField
          register={register}
          error={errors.type}
          options={{
            required: 'This field is required',
            onChange: typeToAddChange,
          }}
          name="type"
          label="Type of item"
        >
          <option value="">Choose a type...</option>
          {typeChoices}
        </SelectField>
      </FormFieldset>
    </form>
  );
};

export const ContentDatabaseItemForm = (props) => {
  // entry point for form
  // if its new, show a selection before loading the form
  // if its existing, show the edit form right away

  // state used based on the currently selected page type
  const [datamodelId, setDatamodelID] = useState(false);

  // wrapper around forms to support adding and editing
  if (props.datamodelItemData) {
    // have an existing datamodel item, show form immediately
    return <ContentDatabaseItemFormDetail {...props} />;
  } else if (datamodelId) {
    // after user has chosen a datamodel type
    const datamodelItemData = {
      datamodel_id: datamodelId,
    };
    return (
      <AddContentDatabaseItemForm
        {...props}
        datamodelItemData={datamodelItemData}
      />
    );
  } else {
    return <SelectContentTypeForm {...props} setDatamodelID={setDatamodelID} />;
  }
};
